/**
 * Helpers for the drawings of the story.
 *
 * Flat vector, cut into layers so the motion code can move each one on its
 * own. Every colour is a class (.fill-*, .stroke-*), mapped to the colour
 * tokens in scenes/_palette.scss: the palette lives in one file.
 *
 * The drawings are built from trusted numbers and class names, so they are
 * plain strings until `scene()` wraps them as markup.
 *
 * @module scenes/svg
 */

import { raw } from '../lib/html.js';

/**
 * An SVG scene.
 *
 * @param {string} viewBox
 * @param {string} body - Inner SVG.
 * @param {string} preserveAspectRatio - How the scene is anchored when cropped.
 * @returns {import('../lib/html.js').Markup}
 */
export function scene(viewBox, body, preserveAspectRatio) {
    return raw(
        `<svg class="scene" viewBox="${viewBox}" preserveAspectRatio="${preserveAspectRatio}" ` +
            `aria-hidden="true" focusable="false">${body}</svg>`,
    );
}

/**
 * A rectangle.
 *
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {string} className - A palette class, or '' when `extra` paints it.
 * @param {string} [extra] - More attributes (rx, opacity, fill…).
 * @returns {string}
 */
export function rect(x, y, width, height, className, extra = '') {
    const attributes = className ? ` class="${className}"` : '';
    return `<rect x="${x}" y="${y}" width="${width}" height="${height}"${attributes}${extra ? ` ${extra}` : ''}/>`;
}

/**
 * A circle.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} r
 * @param {string} [className]
 * @param {string} [extra]
 * @returns {string}
 */
export function circle(cx, cy, r, className = '', extra = '') {
    const attributes = className ? ` class="${className}"` : '';
    return `<circle cx="${cx}" cy="${cy}" r="${r}"${attributes}${extra ? ` ${extra}` : ''}/>`;
}
